import React from "react";
import { X } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { listingPostLabels } from "@/i18n";
import { Card, CardContent } from "@/components/ui/card";
import { ImageUploadButton } from "../ImageUploadButton";
import { ImageGrid } from "../ImageGrid";
import { SortableImageGrid } from "../SortableImageGrid";
import type { VehicleTestImages } from "@/types/vehicleTest";
import type { FileObject } from "@/types/shared/file";
import { photoSessionSourceProps } from "../../photoSession/photoSessionButtonProps";

interface TireSectionProps {
  primaryTires: FileObject[];
  secondaryTires: FileObject[];
  uploadingImages: Set<string>;
  onImageClick: (url: string, alt: string) => void;
  getThumbnailUrl?: (image: any) => string;
  onRemoveImage: (
    section: keyof VehicleTestImages,
    index: number,
    tireType?: "primary" | "secondary",
  ) => void;
  onReorderPrimary?: (newOrder: FileObject[]) => void;
  onReorderSecondary?: (newOrder: FileObject[]) => void;
  onPrimaryFileSelect: (files: File[]) => Promise<void>;
  onPrimarySelect: () => void;
  onPrimaryCameraSelect: () => void;
  onSecondaryFileSelect: (files: File[]) => Promise<void>;
  onSecondarySelect: () => void;
  onSecondaryCameraSelect: () => void;
  onRotate?: (image: any, direction: "left" | "right") => void;
  rotatingImageId?: string | null;
  getImageRotation?: (imageId: string) => number;
}

export const TireSection: React.FC<TireSectionProps> = ({
  primaryTires: primaryTiresProp,
  secondaryTires: secondaryTiresProp,
  uploadingImages,
  onImageClick,
  getThumbnailUrl,
  onRemoveImage,
  onReorderPrimary,
  onReorderSecondary,
  onPrimaryFileSelect,
  onPrimarySelect,
  onPrimaryCameraSelect,
  onSecondaryFileSelect,
  onSecondarySelect,
  onSecondaryCameraSelect,
  onRotate,
  rotatingImageId,
  getImageRotation,
}) => {
  const primaryTires = primaryTiresProp || [];
  const secondaryTires = secondaryTiresProp || [];
  const { language } = useLanguage();
  const imgLabels = listingPostLabels(language).images ?? {};
  const uploadLabel = imgLabels.uploadLabel ?? "Ladda upp bild(er)";
  const maxPerBatch = imgLabels.maxPerBatch5 ?? "(Max 5 åt gången)";
  const sourceProps = photoSessionSourceProps(imgLabels);
  const primaryTiresLabel = imgLabels.primaryTires ?? "Primära däck";
  const secondaryTiresLabel = imgLabels.secondaryTires ?? "Sekundära däck";
  const canAddPrimary = primaryTires.length < 5;
  const canAddSecondary = secondaryTires.length < 5;

  const primaryGrid = onReorderPrimary ? (
    <SortableImageGrid
      images={primaryTires}
      section="tires"
      onImageClick={onImageClick}
      getThumbnailUrl={getThumbnailUrl}
      onRemoveImage={(index) => onRemoveImage("tires", index, "primary")}
      onReorder={onReorderPrimary}
      tireType="primary"
      onRotate={onRotate}
      rotatingImageId={rotatingImageId}
      getImageRotation={getImageRotation}
    >
      {canAddPrimary && (
        <ImageUploadButton
          onSelect={onPrimarySelect}
          onFileSelect={onPrimaryFileSelect}
          onCameraSelect={onPrimaryCameraSelect}
          label={
            <>
              {uploadLabel} <span className="font-semibold">{maxPerBatch}</span>
            </>
          }
          isThumbnail={false}
          disabled={false}
          isLoading={uploadingImages.size > 0}
          section="tires"
          tireType="primary"
          {...sourceProps}
        />
      )}
    </SortableImageGrid>
  ) : (
    <ImageGrid
      images={primaryTires}
      section="tires"
      onImageClick={onImageClick}
      getThumbnailUrl={getThumbnailUrl}
      onRemoveImage={(index) => onRemoveImage("tires", index, "primary")}
      tireType="primary"
      showIndex={primaryTires.length > 0}
    />
  );

  const secondaryGrid = onReorderSecondary ? (
    <SortableImageGrid
      images={secondaryTires}
      section="tires"
      onImageClick={onImageClick}
      getThumbnailUrl={getThumbnailUrl}
      onRemoveImage={(index) => onRemoveImage("tires", index, "secondary")}
      onReorder={onReorderSecondary}
      tireType="secondary"
      onRotate={onRotate}
      rotatingImageId={rotatingImageId}
      getImageRotation={getImageRotation}
    >
      {canAddSecondary && (
        <ImageUploadButton
          onSelect={onSecondarySelect}
          onFileSelect={onSecondaryFileSelect}
          onCameraSelect={onSecondaryCameraSelect}
          label={
            <>
              {uploadLabel} <span className="font-semibold">{maxPerBatch}</span>
            </>
          }
          isThumbnail={false}
          disabled={false}
          isLoading={uploadingImages.size > 0}
          section="tires"
          tireType="secondary"
          {...sourceProps}
        />
      )}
    </SortableImageGrid>
  ) : (
    <ImageGrid
      images={secondaryTires}
      section="tires"
      onImageClick={onImageClick}
      getThumbnailUrl={getThumbnailUrl}
      onRemoveImage={(index) => onRemoveImage("tires", index, "secondary")}
      tireType="secondary"
      showIndex={secondaryTires.length > 0}
    />
  );

  return (
    <Card className="border-0">
      <CardContent className="pt-4">
        <div className="space-y-6">
          {/* Primary Tires */}
          <div>
            <h4 className="font-semibold mb-3">{primaryTiresLabel}</h4>
            {onReorderPrimary ? (
              primaryGrid
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {primaryGrid}

                {canAddPrimary && (
                  <ImageUploadButton
                    onSelect={onPrimarySelect}
                    onFileSelect={onPrimaryFileSelect}
                    onCameraSelect={onPrimaryCameraSelect}
                    label={
                      <>
                        {uploadLabel}{" "}
                        <span className="font-semibold">{maxPerBatch}</span>
                      </>
                    }
                    isThumbnail={false}
                    disabled={false}
                    isLoading={uploadingImages.size > 0}
                    section="tires"
                    tireType="primary"
                    {...sourceProps}
                  />
                )}
              </div>
            )}
          </div>

          {/* Secondary Tires */}
          <div>
            <h4 className="font-semibold mb-3">{secondaryTiresLabel}</h4>
            {onReorderSecondary ? (
              secondaryGrid
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {secondaryGrid}

                {canAddSecondary && (
                  <ImageUploadButton
                    onSelect={onSecondarySelect}
                    onFileSelect={onSecondaryFileSelect}
                    onCameraSelect={onSecondaryCameraSelect}
                    label={
                      <>
                        {uploadLabel}{" "}
                        <span className="font-semibold">{maxPerBatch}</span>
                      </>
                    }
                    isThumbnail={false}
                    disabled={false}
                    isLoading={uploadingImages.size > 0}
                    section="tires"
                    tireType="secondary"
                    {...sourceProps}
                  />
                )}
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
